/**
 * UsageSection — a card that shows one usage section's loading, error, empty or loaded state
 * (SF-5095). Each section owns its own request, so a failed one shows its error here and the
 * rest of the screen carries on. SF-5124: drawn as the shared UsageCard.
 */
import { Alert, Button, Empty, Skeleton } from 'antd';
import { UsageCard } from './UsageCard';

export interface UsageSectionProps {
  title: string;
  /** Optional muted line under the title (e.g. the rule a list follows). */
  subtitle?: React.ReactNode;
  loading: boolean;
  error: boolean;
  onRetry?: () => void;
  /** Loaded but nothing to show. */
  empty?: boolean;
  emptyText?: string;
  /** Optional control on the right of the header. */
  extra?: React.ReactNode;
  bodyStyle?: React.CSSProperties;
  children?: React.ReactNode;
}

export const UsageSection: React.FC<UsageSectionProps> = ({
  title,
  subtitle,
  loading,
  error,
  onRetry,
  empty = false,
  emptyText = 'Nothing this month',
  extra,
  bodyStyle,
  children,
}) => {
  let body: React.ReactNode;
  if (loading) {
    body = <Skeleton active paragraph={{ rows: 4 }} />;
  } else if (error) {
    body = (
      <Alert
        type="error"
        showIcon
        message={`Couldn't load ${title.toLowerCase()}`}
        action={
          onRetry ? (
            <Button size="small" onClick={onRetry}>
              Retry
            </Button>
          ) : null
        }
      />
    );
  } else if (empty) {
    body = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
  } else {
    body = children;
  }

  return (
    <UsageCard title={title} subtitle={subtitle} extra={extra} bodyStyle={bodyStyle}>
      {body}
    </UsageCard>
  );
};
